import Swal from 'sweetalert2';

import type { StoreOrderLineItem, StoreOrderTimeFilterOption } from './customerStoreOrders.types';

export type StoreReceiptDisplayMode = 'download' | 'preview';

/** Türkiye countryId — fiş indirme yalnızca bu ülkede aktif */

export const getCustomerApiCultureCode = (): string => localStorage.getItem('lan') || 'tr-TR';

export const getCustomerApiCountryId = (): number =>
    JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId;

/**
 * Backend TimeFilterToDateTime switch değerleri: LAST_15_DAYS, LAST_30_DAYS, LAST_SIX_MONTH, ALL, YEAR_2026, ...
 */
export const buildStoreOrderTimeFilterParam = (selectedTimeFilter: StoreOrderTimeFilterOption | null): string =>
    selectedTimeFilter?.key?.trim() ?? '';

export const isStoreReceiptDownloadEnabled = (isReceiptDownloadActive: boolean): boolean => isReceiptDownloadActive;

export const stripHtml = (html: string): string => {
    if (!html) {
        return '';
    }
    if (typeof document !== 'undefined') {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent?.trim() ?? html;
    }
    return html.replace(/<[^>]*>/g, '').trim();
};

export const downloadPdfFromBlob = (blob: Blob, fileName: string): void => {
    const pdfUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(pdfUrl);
};

export const previewPdfFromBlob = (blob: Blob, title: string): void => {
    const pdfUrl = URL.createObjectURL(blob);
    const iframeHtml = `<iframe src="${pdfUrl}" title="${title.replace(/"/g, '&quot;')}" style="width:100%;height:70vh;border:none;"></iframe>`;
    void Swal.fire({
        title,
        html: iframeHtml,
        width: 'min(920px, 92vw)',
        showCloseButton: true,
        confirmButtonText: 'OK',
        willClose: () => URL.revokeObjectURL(pdfUrl),
    });
};

export const buildStoreReceiptFileName = (orderNumber: string): string => `StoreReceipt_${orderNumber}.pdf`;

const base64ToUint8Array = (base64: string): Uint8Array => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
};

const isPdfBytes = (bytes: Uint8Array): boolean =>
    bytes.length >= 4 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;

export const gzipBase64ToPdfBlob = async (base64Receipt: string): Promise<Blob> => {
    const compressed: any = base64ToUint8Array(base64Receipt);

    if (isPdfBytes(compressed)) {
        return new Blob([compressed], { type: 'application/pdf' });
    }

    if (typeof DecompressionStream !== 'undefined') {
        const stream = new Blob([compressed]).stream().pipeThrough(new DecompressionStream('gzip'));
        const decompressed = await new Response(stream).arrayBuffer();
        const bytes = new Uint8Array(decompressed);
        const type = isPdfBytes(bytes) ? 'application/pdf' : 'application/octet-stream';
        return new Blob([bytes], { type });
    }

    throw new Error('GZIP decompression is not supported in this browser');
};

export const resolveStoreReceiptBlob = async (response: unknown): Promise<Blob | null> => {
    if (response instanceof Blob) {
        return response;
    }
    if (!response || typeof response !== 'object') {
        return null;
    }
    const root = response as Record<string, unknown>;
    const data = (root.data as Record<string, unknown>) ?? root;
    const receiptB64 = data.receipt;
    if (typeof receiptB64 === 'string' && receiptB64.length > 0) {
        return gzipBase64ToPdfBlob(receiptB64);
    }
    return null;
};

/** API often returns values like "- 200,00 ₺"; UI should not prepend another minus. */
export const formatDiscountAmountLabel = (value: string | undefined | null): string => {
    if (value == null) {
        return '-';
    }
    const trimmed = value.trim();
    if (!trimmed) {
        return '-';
    }
    const withoutLeadingMinus = trimmed.replace(/^[-−–—]\s*/, '');
    return withoutLeadingMinus ? `- ${withoutLeadingMinus}` : '-';
};

export const formatStoreOrderPrice = (amount: number | undefined | null): string => {
    if (amount == null || Number.isNaN(amount)) {
        return '-';
    }
    const currency = import.meta.env.VITE_CURRENCY;
    const formatted = amount.toFixed(2).replace('.', ',');
    return currency ? `${formatted} ${currency}` : formatted;
};

export const hasLineItemDiscount = (item: StoreOrderLineItem): boolean => {
    if (item.listPriceStr && item.salePriceStr && item.listPriceStr !== item.salePriceStr) {
        return true;
    }
    return item.listPrice != null && item.salePrice != null && item.listPrice > item.salePrice;
};

export const formatSizeColorLabel = (size?: string, color?: string): string => {
    const parts = [size, color].filter(Boolean);
    return parts.length ? parts.join(' / ') : '-';
};

export const resolveLineItemPriceDisplay = (
    item: StoreOrderLineItem
): { list: string; sale: string; showDiscount: boolean } => {
    const sale = item.salePriceStr ?? (item.salePrice != null ? formatStoreOrderPrice(item.salePrice) : '-');
    const list = item.listPriceStr ?? (item.listPrice != null ? formatStoreOrderPrice(item.listPrice) : '');
    const showDiscount = hasLineItemDiscount(item);
    return { list, sale, showDiscount };
};
